import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import OpsLayout from '../components/OpsLayout';
import '../css/DoctorManagement.css';
import { API_BASE_URL } from '../config/config';
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx';

// axios 인스턴스 생성
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

// 요청 인터셉터 추가
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

const DoctorManagement = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        major: '',
        field: [],
        educational_background: '',
        career: '',
        admin_id: '',
        adminName: '',
        picture_file_name: '',
        picture_file: '',
        available: true,
        introduction: ''
    });
    const [hospitalInfo, setHospitalInfo] = useState({
        name: '',
        address: '',
        regionCode: '',
        districtCode: ''
    });
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adminSearchResults, setAdminSearchResults] = useState([]);
    const [showAdminDropdown, setShowAdminDropdown] = useState(false);
    const adminInputRef = useRef(null);
    const [adminSearchTerm, setAdminSearchTerm] = useState("");
    const [isEditMode, setIsEditMode] = useState(false);
    const [editDoctorId, setEditDoctorId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalDoctors, setTotalDoctors] = useState(0);
    const pageSize = 10;
    // 진료 분야 옵션을 서버에서 동적으로 불러옴
    const [fieldOptions, setFieldOptions] = useState([]);
    // 진료분야 전체 정보 저장 (엑셀용)
    const [treatmentFields, setTreatmentFields] = useState([]);
    // 의사명 검색 관련 상태 추가
    const [doctorNameSearchTerm, setDoctorNameSearchTerm] = useState("");
    const [doctorNameSearchResults, setDoctorNameSearchResults] = useState([]);
    const [showDoctorNameDropdown, setShowDoctorNameDropdown] = useState(false);
    const doctorNameInputRef = useRef(null);

    // 병원/지역 선택 관련 상태
    const [adminHospitals, setAdminHospitals] = useState([]);
    const [selectedHospital, setSelectedHospital] = useState(null);
    const [showHospitalSelector, setShowHospitalSelector] = useState(false);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [regionOptions, setRegionOptions] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState('');
    const [districtOptions, setDistrictOptions] = useState([]);
    const [selectedDistrict, setSelectedDistrict] = useState('');
    const [hospitalOptions, setHospitalOptions] = useState([]);
    const [hospitalSearchTerm, setHospitalSearchTerm] = useState('');
    const [hospitalSearchResults, setHospitalSearchResults] = useState([]);
    const hospitalInputRef = useRef(null);
    const photoInputRef = useRef(null);
    const bulkUploadInputRef = useRef(null);

    useEffect(() => {
        // 진료분야 목록 불러오기
        const fetchFieldOptions = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${API_BASE_URL}/api/treatment-fields`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (response.data.success && Array.isArray(response.data.fields)) {
                    setFieldOptions(response.data.fields.map(f => ({ value: String(f.id), label: f.name, type: f.type })));
                    setTreatmentFields(response.data.fields); // 전체 정보 저장
                } else {
                    setFieldOptions([]);
                    setTreatmentFields([]);
                }
            } catch (error) {
                setFieldOptions([]);
                setTreatmentFields([]);
            }
        };
        fetchFieldOptions();
    }, []);

    // 시/도 목록 불러오기
    useEffect(() => {
        const fetchRegions = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/regions`);
                if (response.data.success) {
                    setRegionOptions(response.data.regions);
                }
            } catch (e) {
                setRegionOptions([]);
            }
        };
        if (isSuperAdmin) fetchRegions();
    }, [isSuperAdmin]);

    // 구/군 목록 불러오기
    useEffect(() => {
        const fetchDistricts = async () => {
            if (!selectedRegion) {
                setDistrictOptions([]);
                return;
            }
            try {
                const response = await axios.get(`${API_BASE_URL}/api/districts/${encodeURIComponent(selectedRegion)}`);
                console.log('구군 API 응답:', response.data.districts);
                if (response.data.success) {
                    setDistrictOptions(response.data.districts);
                }
            } catch (e) {
                setDistrictOptions([]);
            }
        };
        if (isSuperAdmin && selectedRegion) fetchDistricts();
    }, [isSuperAdmin, selectedRegion]);

    // 병원 목록 불러오기
    useEffect(() => {
        const fetchHospitals = async () => {
            if (!selectedRegion || !selectedDistrict) {
                setHospitalOptions([]);
                return;
            }
            try {
                const params = {
                    region_name: selectedRegion,
                    district_name: selectedDistrict,
                    page: 1,
                    limit: 100
                };
                const response = await axios.get(`${API_BASE_URL}/api/hospitals`, { params });
                if (response.data.success) {
                    setHospitalOptions(response.data.hospitals);
                } else {
                    setHospitalOptions([]);
                }
            } catch (e) {
                setHospitalOptions([]);
            }
        };
        if (isSuperAdmin && selectedRegion && selectedDistrict) fetchHospitals();
    }, [isSuperAdmin, selectedRegion, selectedDistrict]);

    const fetchHospitalInfo = async (hospitalId) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const isSuperAdmin = localStorage.getItem('is_super_admin') === 'true';

            if (isSuperAdmin) {
                setHospitalInfo({
                    name: '전체 병원',
                    address: '전체 지역',
                    regionCode: '',
                    districtCode: ''
                });
                return;
            }

            // 일반 관리자인 경우 localStorage에서 병원/지역 정보 우선 표시
            const hospitalName = localStorage.getItem('hospital_name');
            const regionName = localStorage.getItem('region_name');
            const districtName = localStorage.getItem('district_name');
            if (hospitalName && regionName && districtName) {
                setHospitalInfo({
                    name: hospitalName,
                    address: '',
                    regionCode: regionName,
                    districtCode: districtName
                });
            }

            // 병원 상세 정보도 조회 (주소 등 추가 정보 필요시)
            if (!hospitalId) {
                console.error('병원 ID가 없습니다.');
                toast.error('병원 정보를 불러올 수 없습니다.');
                return;
            }

            const response = await axios.get(
                `${API_BASE_URL}/api/hospitals/detail/${hospitalId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            console.log('병원 상세 응답:', response.data);

            if (response.data.success && response.data.data) {
                const hospitalData = response.data.data;
                setHospitalInfo((prev) => ({
                    ...prev,
                    name: hospitalData.hospital_name || prev.name,
                    address: hospitalData.hospital_address || prev.address,
                    regionCode: hospitalData.region_code || prev.regionCode,
                    districtCode: hospitalData.district_code || prev.districtCode
                }));
            } else {
                console.error('병원 정보 조회 실패:', response.data.message);
                toast.error('병원 정보를 불러오는데 실패했습니다.');
            }
        } catch (error) {
            console.error('병원 정보 조회 중 오류 발생:', error);
            if (error.response) {
                console.error('서버 응답:', error.response.data);
                toast.error(error.response.data.message || '병원 정보를 불러오는데 실패했습니다.');
            } else {
                toast.error('서버와의 통신 중 오류가 발생했습니다.');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const initialize = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                alert('로그인이 필요합니다.');
                navigate('/');
                return;
            }
    
            try {
                const decodedToken = jwtDecode(token);
                const currentTime = Date.now() / 1000;
                if (decodedToken.exp < currentTime) {
                    alert('세션이 만료되었습니다. 다시 로그인해주세요.');
                    localStorage.removeItem('token');
                    navigate('/');
                    return;
                }
    
                const superAdminStatus = localStorage.getItem('is_super_admin') === 'true';
                setIsSuperAdmin(superAdminStatus);
    
                if (superAdminStatus) {
                    setShowHospitalSelector(true);
                    setAdminHospitals([]); // 슈퍼관리자는 병원 직접 검색
                } else { // 일반 관리자
                    const adminGroupIds = JSON.parse(localStorage.getItem('admin_group_ids')) || [];
                    if (adminGroupIds.length > 0) {
                        try {
                            const response = await api.get('/api/admin-groups', { params: { admin_group_ids: adminGroupIds } });
                            if (response.data.success) {
                                const groups = response.data.groups;
                                const hospitalMap = new Map();
                                groups.forEach(g => {
                                    if (g.hospital_id && !hospitalMap.has(g.hospital_id)) {
                                        hospitalMap.set(g.hospital_id, { id: g.hospital_id, name: g.hospital_name });
                                    }
                                });
                                const hospitals = Array.from(hospitalMap.values());
                                setAdminHospitals(hospitals);
                                
                                if (hospitals.length > 1) {
                                    setShowHospitalSelector(true);
                                    fetchDoctors(1); // 여러 병원 소속일 때 초기에 모든 의사 로드
                                } else if (hospitals.length === 1) {
                                    setShowHospitalSelector(false);
                                    const hospitalId = hospitals[0].id;
                                    setSelectedHospital(hospitals[0]);
                                    await fetchHospitalInfo(hospitalId);
                                    fetchDoctors(1, hospitalId);
                                } else {
                                     toast.error('소속된 병원 정보가 없습니다.');
                                     setLoading(false);
                                }
                            } else {
                                toast.error('소속 그룹 정보를 가져오는데 실패했습니다.');
                                setLoading(false);
                            }
                        } catch(error) {
                            toast.error("그룹 정보 로딩 중 오류 발생");
                            setLoading(false);
                        }
                    } else {
                        setLoading(false);
                        toast.error('소속된 관리자 그룹이 없어 데이터를 조회할 수 없습니다.');
                    }
                }
            } catch (error) {
                console.error('토큰 검증 실패:', error);
                alert('인증에 실패했습니다. 다시 로그인해주세요.');
                localStorage.removeItem('token');
                navigate('/');
            }
        };
    
        initialize();
    }, [navigate]);

    const handleHospitalDropdownChange = async (e) => {
        const hospitalId = e.target.value;
        if (hospitalId) {
            const hospital = adminHospitals.find(h => h.id === parseInt(hospitalId, 10));
            setSelectedHospital(hospital);
            await fetchHospitalInfo(hospitalId);
            fetchDoctors(1, hospitalId);
        } else {
            setSelectedHospital(null);
            setDoctors([]);
            setHospitalInfo({ name: '', address: '' });
        }
    };

    const fetchDoctors = async (page = 1, hospitalId = null) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            let params = { page, limit: pageSize };

            if (isSuperAdmin) {
                params.is_super_admin = 'true';
                if (selectedHospital) {
                    params.hospital_id = selectedHospital.id;
                } else {
                    setDoctors([]);
                    setTotalPages(1);
                    setCurrentPage(1);
                    setTotalDoctors(0);
                    setLoading(false);
                    return;
                }
            } else {
                if (hospitalId) {
                    params.hospital_id = hospitalId;
                } else {
                    const adminGroupIds = JSON.parse(localStorage.getItem('admin_group_ids')) || [];
                    if (adminGroupIds.length > 0) {
                        params.admin_group_ids = adminGroupIds.join(',');
                    } else {
                        setDoctors([]);
                        setTotalPages(1);
                        setCurrentPage(1);
                        setTotalDoctors(0);
                        setLoading(false);
                        return;
                    }
                }
            }
            
            console.log('Fetching doctors with params:', params);
            
            const response = await axios.get(`${API_BASE_URL}/api/doctors`, {
                params,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            console.log('API Response:', response.data);

            if (response.data.success) {
                setDoctors(response.data.doctors);
                setTotalPages(response.data.totalPages || 1);
                setCurrentPage(response.data.currentPage || 1);
                setTotalDoctors(response.data.totalCount || response.data.doctors.length);
            } else {
                console.error('Failed to fetch doctors:', response.data.error);
            }
        } catch (error) {
            console.error('Error fetching doctors:', error);
            if (error.response) {
                console.error('Error response:', {
                    status: error.response.status,
                    data: error.response.data
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAdminIdInputChange = (e) => {
        setAdminSearchTerm(e.target.value);
        setFormData(prev => ({ ...prev, admin_id: e.target.value, adminName: '' }));
        setAdminSearchResults([]);
        setShowAdminDropdown(false);
    };

    const handleAdminSearch = async () => {
        const term = adminSearchTerm.trim();
        console.log('검색어:', term);
        
        if (!term) {
            toast.error('검색어를 입력해주세요.');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                toast.error('로그인이 필요합니다.');
                navigate('/login');
                return;
            }

            const hospitalId = localStorage.getItem('hospital_id');
            const regionCode = hospitalInfo?.regionCode || hospitalInfo?.region_code;
            const districtCode = hospitalInfo?.districtCode || hospitalInfo?.district_code;
            const groupId = localStorage.getItem('admin_group_id');
            
            console.log('검색 파라미터:', { 
                term, 
                hospitalId, 
                regionCode, 
                districtCode,
                groupId
            });
            
            if (!hospitalId || !regionCode || !districtCode) {
                toast.error('병원 정보가 없습니다.');
                setAdminSearchResults([]);
                setShowAdminDropdown(false);
                return;
            }

            if (!groupId) {
                toast.error('관리자 그룹 정보가 없습니다.');
                setAdminSearchResults([]);
                setShowAdminDropdown(false);
                return;
            }

            // 파라미터를 문자열로 변환하여 전송
            const params = {
                term,
                hospital_id: String(hospitalId),
                region_code: String(regionCode),
                district_code: String(districtCode),
                group_id: String(groupId)
            };

            console.log('전송할 파라미터:', params);
            console.log('API URL:', `${API_BASE_URL}/api/admin-users/search`);

            const response = await axios.get(`${API_BASE_URL}/api/admin-users/search`, {
                params,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            
            console.log('검색 결과:', response.data);
            
            if (response.data.success) {
                const users = response.data.users || [];
                if (users.length === 0) {
                    toast.info('검색 결과가 없습니다.');
                }
                setAdminSearchResults(users);
                setShowAdminDropdown(true);
            } else {
                console.log('검색 실패:', response.data.message);
                toast.error(response.data.message || '검색에 실패했습니다.');
                setAdminSearchResults([]);
                setShowAdminDropdown(false);
            }
        } catch (error) {
            console.error('검색 오류:', error);
            if (error.response) {
                console.error('에러 응답:', {
                    status: error.response.status,
                    data: error.response.data,
                    headers: error.response.headers,
                    config: {
                        url: error.config.url,
                        method: error.config.method,
                        params: error.config.params,
                        headers: error.config.headers
                    }
                });
                toast.error(error.response.data.message || '검색 중 오류가 발생했습니다.');
            } else if (error.request) {
                console.error('요청 오류:', error.request);
                toast.error('서버와의 통신에 실패했습니다.');
            } else {
                console.error('에러 메시지:', error.message);
                toast.error('요청 처리 중 오류가 발생했습니다.');
            }
            setAdminSearchResults([]);
            setShowAdminDropdown(false);
        }
    };

    const handleAdminInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAdminSearch();
        }
    };

    const handleAdminSelect = (user) => {
        console.log('Selected user:', user);
        setFormData(prev => ({ ...prev, admin_id: user.user_id, adminName: user.name }));
        setAdminSearchTerm(user.email);
        setShowAdminDropdown(false);
    };

    // 드롭다운 외부 클릭 시 닫기
    useEffect(() => {
        function handleClickOutside(event) {
            if (adminInputRef.current && !adminInputRef.current.contains(event.target)) {
                setShowAdminDropdown(false);
            }
        }
        if (showAdminDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showAdminDropdown]);

    const handleEditDoctor = (doctor) => {
        setFormData({
            name: doctor.name || '',
            major: doctor.major || '',
            field: Array.isArray(doctor.field) ? doctor.field.map(f => String(f.id)) : [],
            educational_background: doctor.educational_background || '',
            career: doctor.career || '',
            admin_id: doctor.admin_id || null,
            adminName: doctor.adminName || '',
            picture_file_name: doctor.picture_file_name || '',
            picture_file: doctor.picture_file || '',
            available: doctor.available === true || doctor.available === 'true',
            introduction: doctor.introduction || ''
        });
        setIsEditMode(true);
        setEditDoctorId(doctor.id);
        setAdminSearchTerm('');
        setDoctorNameSearchTerm(doctor.name || '');

        // admin_id 체크 로직 수정 - null 값 허용
        if (doctor.admin_id === null) {
            toast.info('이 의사 정보에는 관리자가 지정되어 있지 않습니다. 필요한 경우 관리자를 검색하여 선택할 수 있습니다.');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // admin_id 체크 로직 수정 - null 값 허용
        if (formData.admin_id === '') {
            toast.error('관리자 ID가 비어있습니다. 관리자 검색 후 선택하거나, 관리자 없음을 확인해 주세요.');
            return;
        }

        const maxRetries = 3;
        const retryDelay = 1000;

        for (let retryCount = 0; retryCount < maxRetries; retryCount++) {
            try {
                const token = localStorage.getItem('token');
                const hospitalId = localStorage.getItem('hospital_id');
                const formDataToSend = new FormData();
                
                // 테이블 컬럼명에 맞게 데이터 매핑
                formDataToSend.append('name', formData.name);
                formDataToSend.append('major', formData.major);
                formDataToSend.append('field', formData.field.join(','));
                formDataToSend.append('educational_background', formData.educational_background);
                formDataToSend.append('career', formData.career);
                // admin_id가 null이거나 빈 문자열이면 필드를 제외
                if (formData.admin_id && formData.admin_id !== '') {
                    formDataToSend.append('admin_id', formData.admin_id);
                }
                formDataToSend.append('hospital_id', hospitalId);
                formDataToSend.append('available', formData.available ? 'true' : 'false');
                formDataToSend.append('introduction', formData.introduction || '');

                if (formData.doctorPhoto) {
                    const reader = new FileReader();
                    reader.readAsDataURL(formData.doctorPhoto);
                    reader.onload = async () => {
                        const base64Data = reader.result.split(',')[1];
                        formDataToSend.append('picture_file_name', formData.doctorPhoto.name);
                        formDataToSend.append('picture_file', base64Data);
                        if (isEditMode && editDoctorId) {
                            await updateDoctor(editDoctorId, formDataToSend, token);
                        } else {
                            await createDoctor(formDataToSend, token);
                        }
                    };
                    return;
                } else {
                    if (isEditMode && editDoctorId) {
                        if (formData.picture_file_name) {
                            formDataToSend.append('picture_file_name', formData.picture_file_name);
                        }
                        if (formData.picture_file) {
                            formDataToSend.append('picture_file', formData.picture_file);
                        }
                        await updateDoctor(editDoctorId, formDataToSend, token);
                    } else {
                        await createDoctor(formDataToSend, token);
                    }
                }
                break;
            } catch (error) {
                console.error('주치의 등록 실패:', error);
                
                if (retryCount < maxRetries - 1) {
                    console.log(`재시도 ${retryCount + 1}/${maxRetries}...`);
                    await new Promise(resolve => setTimeout(resolve, retryDelay * (retryCount + 1)));
                    continue;
                }

                if (error.response) {
                    toast.error(error.response.data.message || '주치의 등록에 실패했습니다.');
                } else if (error.request) {
                    toast.error('서버와의 통신에 실패했습니다.');
                } else {
                    toast.error('요청 처리 중 오류가 발생했습니다.');
                }
            }
        }
    };

    const createDoctor = async (formDataToSend, token) => {
        formDataToSend.set('field', formData.field.join(','));
        formDataToSend.set('introduction', formData.introduction || '');
        const response = await axios.post(`${API_BASE_URL}/api/doctors/register`, formDataToSend, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
            }
        });
        if (response.data.success) {
            toast.success('주치의 정보가 등록되었습니다.');
            resetForm();
            fetchDoctors(1);
        }
    };

    const updateDoctor = async (doctorId, formDataToSend, token) => {
        formDataToSend.set('field', formData.field.join(','));
        formDataToSend.set('introduction', formData.introduction || '');
        // admin_id가 null이거나 빈 문자열이면 필드를 제외
        if (formData.admin_id && formData.admin_id !== '') {
            formDataToSend.set('admin_id', formData.admin_id);
        }
        
        const response = await axios.put(`${API_BASE_URL}/api/doctors/${doctorId}`, formDataToSend, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
            }
        });
        if (response.data.success) {
            toast.success('주치의 정보가 수정되었습니다.');
            resetForm();
            fetchDoctors(1);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            major: '',
            field: [],
            educational_background: '',
            career: '',
            admin_id: '',
            adminName: '',
            picture_file_name: '',
            picture_file: '',
            available: true,
            introduction: ''
        });
        setIsEditMode(false);
        setEditDoctorId(null);
        setAdminSearchTerm('');
        setDoctorNameSearchTerm('');
    };

    const handleDownloadTemplate = () => {
        // 엑셀 워크북 생성
        const wb = XLSX.utils.book_new();
        // 헤더 데이터 생성
        const headers = [
            '의사명',
            '전공',
            '진료분야',
            '학력',
            '경력',
            '자기 소개',
            '', // 안내문구용 빈 셀
        ];
        // 안내문구 추가 (경력 오른쪽 셀)
        const guide = '진료분야 정보는 "진료분야 목록" 시트를 참고하여 해당 진료분야의 ID를 작성해 주세요. 여러 개일 경우 쉼표(,)로 구분해 주세요.';
        // 워크시트 생성 (첫 행에 안내문구 포함)
        const ws = XLSX.utils.aoa_to_sheet([
            headers,
            ['', '', '', '', '', '', guide]
        ]);
        XLSX.utils.book_append_sheet(wb, ws, '의사 정보');

        // 진료분야(ID, name) 시트 추가
        if (treatmentFields.length > 0) {
            const fieldSheetData = [['ID', '진료분야명']].concat(
                treatmentFields.map(f => [f.id, f.name])
            );
            const ws2 = XLSX.utils.aoa_to_sheet(fieldSheetData);
            XLSX.utils.book_append_sheet(wb, ws2, '진료분야 목록');
        }
        // 파일 다운로드
        XLSX.writeFile(wb, '의사_정보_등록_양식.xlsx');
    };

    const handleBulkUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            const reader = new FileReader();
            reader.onload = async (e) => {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                
                // 첫 번째 시트 가져오기
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);
                
                // 데이터 유효성 검사
                const validData = jsonData.filter(row => 
                    row['의사명'] && 
                    row['전공'] && 
                    row['진료분야'] && 
                    row['학력']
                );

                if (validData.length === 0) {
                    toast.error('유효한 데이터가 없습니다.');
                    return;
                }

                // 서버로 데이터 전송
                const token = localStorage.getItem('token');
                const hospitalId = localStorage.getItem('hospital_id');

                const response = await axios.post(
                    `${API_BASE_URL}/api/doctors/bulk`,
                    {
                        doctors: validData.map(row => ({
                            name: row['의사명'],
                            major: row['전공'],
                            field: row['진료분야'],
                            educational_background: row['학력'],
                            career: row['경력'] || null,
                            introduction: row['자기 소개'] || null,
                            hospital_id: hospitalId,
                            available: true
                        }))
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    }
                );

                if (response.data.success) {
                    toast.success('의사 정보가 일괄 등록되었습니다.');
                    fetchDoctors(1); // 의사 목록 새로고침
                } else {
                    toast.error(response.data.message || '의사 정보 등록에 실패했습니다.');
                }
            };
            reader.readAsArrayBuffer(file);
        } catch (error) {
            console.error('파일 처리 중 오류 발생:', error);
            if (error.response) {
                toast.error(error.response.data.message || '파일 처리 중 오류가 발생했습니다.');
            } else {
                toast.error('파일 처리 중 오류가 발생했습니다.');
            }
        }
    };

    const getImageMimeType = (fileName) => {
        if (!fileName) return 'image/jpeg';
        const ext = fileName.split('.').pop().toLowerCase();
        if (ext === 'png') return 'image/png';
        if (ext === 'gif') return 'image/gif';
        if (ext === 'bmp') return 'image/bmp';
        return 'image/jpeg';
    };

    const handleDeleteDoctor = async (doctorId) => {
        if (!window.confirm('정말로 이 의사 정보를 삭제하시겠습니까?')) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await axios.delete(`${API_BASE_URL}/api/doctors/${doctorId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.data.success) {
                toast.success('의사 정보가 삭제되었습니다.');
                await fetchDoctors(1); // 목록 새로고침을 기다린 뒤
                resetForm(); // 폼 초기화
            } else {
                toast.error(response.data.message || '의사 정보 삭제에 실패했습니다.');
            }
        } catch (error) {
            console.error('의사 정보 삭제 실패:', error);
            if (error.response) {
                toast.error(error.response.data.message || '의사 정보 삭제에 실패했습니다.');
            } else {
                toast.error('서버와의 통신 중 오류가 발생했습니다.');
            }
        }
    };

    // 페이지 변경 핸들러
    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
        fetchDoctors(page);
    };

    // 진료분야 다중 선택 핸들러 (id 배열로 저장)
    const handleFieldChange = (e) => {
        const options = e.target.options;
        const selectedValues = [];
        for (let i = 0; i < options.length; i++) {
            if (options[i].selected) {
                selectedValues.push(options[i].value);
            }
        }
        setFormData(prev => ({
            ...prev,
            field: selectedValues
        }));
    };

    // 의사명으로 관리자 검색
    const handleDoctorNameSearch = async () => {
        const term = doctorNameSearchTerm.trim();
        if (!term) {
            toast.error('의사명을 입력해주세요.');
            return;
        }
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                toast.error('로그인이 필요합니다.');
                navigate('/login');
                return;
            }
            const hospitalId = localStorage.getItem('hospital_id');
            const regionCode = hospitalInfo?.regionCode || hospitalInfo?.region_code;
            const districtCode = hospitalInfo?.districtCode || hospitalInfo?.district_code;
            const groupId = localStorage.getItem('admin_group_id');
            if (!hospitalId || !regionCode || !districtCode) {
                toast.error('병원 정보가 없습니다.');
                setDoctorNameSearchResults([]);
                setShowDoctorNameDropdown(false);
                return;
            }
            if (!groupId) {
                toast.error('관리자 그룹 정보가 없습니다.');
                setDoctorNameSearchResults([]);
                setShowDoctorNameDropdown(false);
                return;
            }
            const params = {
                term,
                hospital_id: String(hospitalId),
                region_code: String(regionCode),
                district_code: String(districtCode),
                group_id: String(groupId)
            };
            const response = await axios.get(`${API_BASE_URL}/api/admin-users/search`, {
                params,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            if (response.data.success) {
                const users = response.data.users || [];
                if (users.length === 0) {
                    toast.info('검색 결과가 없습니다.');
                }
                setDoctorNameSearchResults(users);
                setShowDoctorNameDropdown(true);
            } else {
                toast.error(response.data.message || '검색에 실패했습니다.');
                setDoctorNameSearchResults([]);
                setShowDoctorNameDropdown(false);
            }
        } catch (error) {
            toast.error('검색 중 오류가 발생했습니다.');
            setDoctorNameSearchResults([]);
            setShowDoctorNameDropdown(false);
        }
    };
    // 의사명 드롭다운에서 선택
    const handleDoctorNameSelect = (user) => {
        setFormData(prev => ({ ...prev, name: user.name, admin_id: user.user_id }));
        setDoctorNameSearchTerm(user.name);
        setShowDoctorNameDropdown(false);
    };
    // 드롭다운 외부 클릭 시 닫기
    useEffect(() => {
        function handleClickOutside(event) {
            if (doctorNameInputRef.current && !doctorNameInputRef.current.contains(event.target)) {
                setShowDoctorNameDropdown(false);
            }
        }
        if (showDoctorNameDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDoctorNameDropdown]);

    // 병원명 검색 (자동완성)
    const handleHospitalSearch = async (term) => {
        if (!selectedRegion) {
            toast.error('지역을 먼저 선택하세요.');
            return;
        }
        try {
            const token = localStorage.getItem('token');
            const params = {
                region_name: selectedRegion,
                district_name: selectedDistrict,
                search_term: term
            };
            const response = await axios.get(`${API_BASE_URL}/api/hospitals/search`, {
                params,
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.data.success) {
                setHospitalSearchResults(response.data.hospitals);
            } else {
                setHospitalSearchResults([]);
            }
        } catch (e) {
            setHospitalSearchResults([]);
        }
    };

    // 슈퍼관리자 병원 선택
    const handleSuperAdminHospitalSelect = (hospital) => {
        setSelectedHospital(hospital);
        setHospitalSearchTerm(hospital.institution_name);
        setHospitalSearchResults([]);
        setHospitalInfo(prev => ({
            ...prev,
            name: hospital.institution_name,
            address: hospital.address || '',
            regionCode: hospital.region_code || '',
            districtCode: hospital.district_code || ''
        }));
        fetchDoctors(1, hospital.id);
    };

    // 고른 진료분야를 칩으로 보여 주기 위한 이름 목록. field 는 id 문자열 배열이다.
    const selectedSpecialties = formData.field
        .map((id) => fieldOptions.find((option) => option.value === id)?.label)
        .filter(Boolean);

    // 칩의 선택 해제 — 기존 선택 상태(formData.field)에서 해당 id 를 뺀다.
    const removeSpecialty = (label) => {
        const option = fieldOptions.find((opt) => opt.label === label);
        if (!option) return;
        setFormData(prev => ({ ...prev, field: prev.field.filter(v => v !== option.value) }));
    };

    return (
        <OpsLayout
            title="주치의 정보 등록/변경"
            description="소속 병원의 주치의를 등록하고 고쳐요."
            actions={
                <>
                    <button className="sds-btn sds-btn--secondary" onClick={handleDownloadTemplate}>
                        양식 다운로드
                    </button>
                    <button
                        className="sds-btn sds-btn--secondary"
                        onClick={() => bulkUploadInputRef.current && bulkUploadInputRef.current.click()}
                    >
                        일괄 업로드
                    </button>
                    <input
                        ref={bulkUploadInputRef}
                        type="file"
                        accept=".xlsx"
                        onChange={handleBulkUpload}
                        style={{ display: 'none' }}
                    />
                </>
            }
        >
            <section className="ops-page__card doctors-page__form">
                <div className="ops-page__card-title">
                    <h2>주치의 정보</h2>
                    {isEditMode && <small>편집 중: {formData.name}</small>}
                </div>

                <form id="doctorForm" onSubmit={handleSubmit}>
                    <div className="doctors-page__body">
                        <div className="doctors-page__photo">
                            <div className="doctors-page__photo-preview">
                                {formData.doctorPhoto ? (
                                    <img src={URL.createObjectURL(formData.doctorPhoto)} alt="의사 사진 미리보기" />
                                ) : formData.picture_file ? (
                                    <img
                                        src={`data:${getImageMimeType(formData.picture_file_name)};base64,${formData.picture_file}`}
                                        alt="의사 사진 미리보기"
                                    />
                                ) : (
                                    <span className="doctors-page__photo-empty">사진 없음</span>
                                )}
                            </div>
                            <button
                                className="sds-btn sds-btn--secondary sds-btn--sm"
                                onClick={(e) => { e.preventDefault(); photoInputRef.current && photoInputRef.current.click(); }}
                            >
                                사진 변경
                            </button>
                            <input
                                ref={photoInputRef}
                                type="file"
                                name="doctorPhoto"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={e => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        setFormData(prev => ({
                                            ...prev,
                                            doctorPhoto: file
                                        }));
                                    }
                                }}
                            />
                        </div>

                        <div className="doctors-page__grid">
                            <div className="sds-field doctors-page__field-name">
                                <label className="sds-label">의사명<span className="sds-required">*</span></label>
                                <div className="doctors-page__name-row" ref={doctorNameInputRef}>
                                    <input
                                        className="sds-input"
                                        name="name"
                                        value={doctorNameSearchTerm}
                                        onChange={e => setDoctorNameSearchTerm(e.target.value)}
                                        placeholder="의사명으로 검색"
                                        autoComplete="off"
                                        required
                                    />
                                    <button
                                        className="sds-btn sds-btn--secondary"
                                        onClick={(e) => { e.preventDefault(); handleDoctorNameSearch(); }}
                                    >
                                        검색
                                    </button>
                                    {showDoctorNameDropdown && doctorNameSearchResults.length > 0 && (
                                        <ul className="doctors-page__suggest">
                                            {doctorNameSearchResults.map((user, idx) => (
                                                <li
                                                    key={user.user_id || user.email || idx}
                                                    className={formData.admin_id === user.user_id ? 'is-active' : undefined}
                                                    onClick={() => handleDoctorNameSelect(user)}
                                                >
                                                    <span className="doctors-page__suggest-name">{user.name}</span>
                                                    <span className="doctors-page__suggest-email">({user.email})</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>

                            <div className="sds-field">
                                <label className="sds-label">전공<span className="sds-required">*</span></label>
                                <input
                                    className="sds-input"
                                    name="major"
                                    value={formData.major}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className="sds-field">
                                <label className="sds-label">사용 가능 여부</label>
                                <div className="sds-seg" role="radiogroup" aria-label="사용 가능 여부">
                                    {[[true, '사용'], [false, '미사용']].map(([value, label]) => {
                                        const active = formData.available === value;
                                        return (
                                            <button
                                                key={label}
                                                role="radio"
                                                aria-checked={active}
                                                className={`sds-seg__item${active ? ' is-active' : ''}`}
                                                onClick={(e) => { e.preventDefault(); setFormData(prev => ({ ...prev, available: value })); }}
                                            >
                                                {label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="sds-field doctors-page__field-wide">
                                <label className="sds-label">소속병원</label>
                                {isSuperAdmin ? (
                                    <div className="doctors-page__hospital-row">
                                        <select
                                            className="sds-select"
                                            value={selectedRegion}
                                            onChange={e => { setSelectedRegion(e.target.value); setSelectedDistrict(''); setHospitalSearchTerm(''); setHospitalSearchResults([]); setSelectedHospital(null); }}
                                        >
                                            <option value="">시/도 선택</option>
                                            {regionOptions.map(r => (
                                                <option key={r.region_code || r.region_name} value={r.region_name}>{r.region_name}</option>
                                            ))}
                                        </select>
                                        <select
                                            className="sds-select"
                                            value={selectedDistrict}
                                            onChange={e => { setSelectedDistrict(e.target.value); setHospitalSearchTerm(''); setHospitalSearchResults([]); setSelectedHospital(null); }}
                                            disabled={!selectedRegion}
                                        >
                                            <option value="">구/군 선택</option>
                                            {districtOptions.map(d => (
                                                <option key={d.district_code} value={d.district_name}>{d.district_name}</option>
                                            ))}
                                        </select>
                                        <div className="doctors-page__hospital-search">
                                            <input
                                                ref={hospitalInputRef}
                                                className="sds-input"
                                                placeholder="병원명 검색"
                                                value={hospitalSearchTerm}
                                                onChange={e => {
                                                    setHospitalSearchTerm(e.target.value);
                                                    if (selectedHospital && e.target.value === selectedHospital.institution_name) {
                                                        setHospitalSearchResults([]);
                                                        return;
                                                    }
                                                    setSelectedHospital(null);
                                                    if (e.target.value.length > 1) handleHospitalSearch(e.target.value);
                                                    else setHospitalSearchResults([]);
                                                }}
                                                onBlur={() => setHospitalSearchResults([])}
                                                disabled={!selectedRegion || !selectedDistrict}
                                            />
                                            {hospitalSearchResults.length > 0 && (
                                                <ul className="doctors-page__suggest">
                                                    {hospitalSearchResults.map(h => (
                                                        <li
                                                            key={h.id}
                                                            onClick={() => {
                                                                handleSuperAdminHospitalSelect(h);
                                                                setHospitalSearchResults([]);
                                                                setHospitalSearchTerm(h.institution_name);
                                                                if (hospitalInputRef.current) hospitalInputRef.current.blur();
                                                            }}
                                                        >
                                                            {h.institution_name}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <select
                                        className="sds-select"
                                        value={selectedHospital?.id || ''}
                                        onChange={handleHospitalDropdownChange}
                                    >
                                        <option value="">병원 선택</option>
                                        {adminHospitals.map(h => <option key={h.id} value={h.id}>{h.institution_name || h.name}</option>)}
                                    </select>
                                )}
                            </div>

                            <div className="sds-field">
                                <label className="sds-label">소속병원주소</label>
                                <input className="sds-input" value={hospitalInfo.address || ''} disabled />
                            </div>

                            <div className="sds-field doctors-page__field-wide">
                                <label className="sds-label">진료분야<span className="sds-required">*</span></label>
                                <div className="doctors-page__chips" aria-live="polite">
                                    {selectedSpecialties.length === 0
                                        ? <span className="doctors-page__chips-empty">고른 진료분야가 없어요</span>
                                        : selectedSpecialties.map((s) => (
                                            <button
                                                key={s}
                                                className="sds-badge sds-badge--info doctors-page__chip"
                                                onClick={(e) => { e.preventDefault(); removeSpecialty(s); }}
                                                aria-label={`${s} 선택 해제`}
                                            >
                                                {s}<X size={16} aria-hidden="true" />
                                            </button>
                                        ))}
                                </div>
                                <select
                                    multiple
                                    name="field"
                                    value={formData.field}
                                    onChange={handleFieldChange}
                                    required
                                    className="sds-select doctors-page__field-select"
                                >
                                    {fieldOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                <p className="doctors-page__field-guide">
                                    마우스로 클릭하거나 드래그하여 여러 항목을 선택할 수 있어요.<br />
                                    Ctrl 키 또는 Shift 키를 누른 상태로 클릭하면 여러 항목을 개별 또는 범위로 선택할 수 있어요.<br />
                                    여러 항목을 선택하면 위 칩으로 함께 표시돼요.
                                </p>
                            </div>

                            <div className="sds-field">
                                <label className="sds-label">학력<span className="sds-required">*</span></label>
                                <input
                                    className="sds-input"
                                    name="educational_background"
                                    value={formData.educational_background}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className="sds-field doctors-page__field-wide">
                                <label className="sds-label">경력</label>
                                <textarea
                                    className="sds-textarea"
                                    name="career"
                                    value={formData.career}
                                    onChange={handleInputChange}
                                    placeholder="경력을 입력하세요"
                                    rows={4}
                                />
                            </div>

                            <div className="sds-field doctors-page__field-wide">
                                <label className="sds-label">자기 소개</label>
                                <textarea
                                    className="sds-textarea"
                                    name="introduction"
                                    value={formData.introduction}
                                    onChange={handleInputChange}
                                    placeholder="자기 소개를 입력하세요"
                                    rows={4}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="sds-modal__foot">
                        <button className="sds-btn sds-btn--primary">
                            {isEditMode ? '수정' : '저장'}
                        </button>
                    </div>
                </form>
            </section>

            <section className="ops-page__card">
                <div className="ops-page__card-title">
                    <h2>주치의 현황</h2>
                    <small>{totalDoctors}명</small>
                </div>

                {loading ? (
                    <div className="sds-notice">데이터를 불러오는 중...</div>
                ) : isSuperAdmin && !selectedHospital ? (
                    <div className="sds-notice">병원을 먼저 선택해 주세요.</div>
                ) : (
                    <div className="sds-table-wrap">
                        <table className="sds-table">
                            <thead>
                                <tr>
                                    <th>사진</th>
                                    <th>의사명</th>
                                    <th>전공</th>
                                    <th>진료분야</th>
                                    <th>학력</th>
                                    <th className="sds-table__center">사용 가능 여부</th>
                                    <th>등록일</th>
                                    <th className="sds-table__center">수정</th>
                                </tr>
                            </thead>
                            <tbody>
                                {doctors.length > 0 ? (
                                    doctors.map((doctor, index) => {
                                        const available = doctor.available === true || doctor.available === 'true';
                                        return (
                                            <tr
                                                key={doctor.id ?? index}
                                                className={isEditMode && editDoctorId === doctor.id ? 'is-selected' : undefined}
                                            >
                                                <td>
                                                    {doctor.picture_file ? (
                                                        <img
                                                            className="doctors-page__thumb"
                                                            src={`data:${getImageMimeType(doctor.picture_file_name)};base64,${doctor.picture_file}`}
                                                            alt=""
                                                            onError={(e) => {
                                                                if (!e.target.src.startsWith('data:image/png')) {
                                                                    e.target.src = `data:image/png;base64,${doctor.picture_file}`;
                                                                }
                                                            }}
                                                        />
                                                    ) : (
                                                        <span className="doctors-page__thumb doctors-page__thumb--empty" aria-hidden="true" />
                                                    )}
                                                </td>
                                                <td>{doctor.name}</td>
                                                <td>{doctor.major}</td>
                                                <td>
                                                    {Array.isArray(doctor.field) && doctor.field.length > 0
                                                        ? doctor.field.map(f => f.name).join(', ')
                                                        : '-'}
                                                </td>
                                                <td>{doctor.educational_background}</td>
                                                <td className="sds-table__center">
                                                    <span className={`sds-badge sds-badge--${available ? 'normal' : 'none'}`}>
                                                        {available ? '사용' : '미사용'}
                                                    </span>
                                                </td>
                                                <td>
                                                    {doctor.created_at
                                                        ? new Date(doctor.created_at).toLocaleDateString()
                                                        : '-'}
                                                </td>
                                                <td className="sds-table__center">
                                                    <div className="sds-row-actions">
                                                        <button className="sds-btn sds-btn--weak sds-btn--sm" onClick={() => handleEditDoctor(doctor)}>편집</button>
                                                        <button className="sds-btn sds-btn--ghost sds-btn--sm" onClick={() => handleDeleteDoctor(doctor.id)}>삭제</button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={8} className="sds-empty">등록된 정보가 없습니다.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {doctors.length > 0 && (
                    <div className="sds-pagination">
                        <button
                            className="sds-pagination__btn"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            aria-label="이전 페이지"
                        >
                            <ChevronLeft size={16} aria-hidden="true" />
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                            <button
                                key={page}
                                className={`sds-pagination__btn${currentPage === page ? ' is-active' : ''}`}
                                aria-current={currentPage === page ? 'page' : undefined}
                                onClick={() => handlePageChange(page)}
                            >
                                {page}
                            </button>
                        ))}
                        <button
                            className="sds-pagination__btn"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            aria-label="다음 페이지"
                        >
                            <ChevronRight size={16} aria-hidden="true" />
                        </button>
                    </div>
                )}
            </section>
        </OpsLayout>
    );
};

export default DoctorManagement; 